import React from "react";

export interface EntityCardDetail {
  /** Optional short label, shown as `label · value`. Omit for a bare value line. */
  label?: string;
  value: React.ReactNode;
}

export interface EntityCardProps {
  /** Primary name / heading, shown top-left (bold). */
  title: React.ReactNode;
  /** Long text shown as a wrapping paragraph directly under the title. */
  description?: React.ReactNode;
  /** Secondary fields as compact `label · value` muted lines under the description. */
  details?: EntityCardDetail[];
  /** Status pill / badge — shown on its own line at the bottom of the card. */
  status?: React.ReactNode;
  /** Row actions — typically a `<TableActions>` element (its ⋮ kebab on mobile). */
  actions?: React.ReactNode;
  /** A prominent primary action (e.g. a full-width button) rendered at the card's bottom. */
  primaryAction?: React.ReactNode;
  /** Accessible label for the card, e.g. `Church Bethel`. */
  ariaLabel?: string;
  testId?: string;
  /** When set, the title becomes a button that triggers this (mirrors table row click). */
  onActivate?: () => void;
}

/**
 * Mobile equivalent of a single table row — a clean list item rather than a form.
 *
 * Anatomy (uniform across all tables): bold title top-left with a borderless ⋮
 * kebab top-right; description wraps directly beneath the title with no label;
 * any extra fields read as compact `label · value` muted lines; the status pill
 * sits on its own line at the bottom. Pairs with the desktop `<table>` in the
 * responsive table→card transform.
 */
const EntityCard: React.FC<EntityCardProps> = ({
  title,
  description,
  details,
  status,
  actions,
  primaryAction,
  ariaLabel,
  testId,
  onActivate,
}) => {
  const titleNode = onActivate ? (
    <button type="button" className="ntcog-entity-card__title-btn" onClick={onActivate}>
      {title}
    </button>
  ) : (
    title
  );

  const visibleDetails = (details ?? []).filter((d) => d.value !== null && d.value !== undefined && d.value !== "");

  return (
    <article className="ntcog-entity-card" aria-label={ariaLabel} data-testid={testId}>
      <div className="ntcog-entity-card__header">
        <div className="ntcog-entity-card__title">{titleNode}</div>
        {actions && <div className="ntcog-entity-card__aside">{actions}</div>}
      </div>

      {description && <p className="ntcog-entity-card__desc text-break mb-0">{description}</p>}

      {visibleDetails.length > 0 && (
        <div className="ntcog-entity-card__details">
          {visibleDetails.map((d, i) => (
            <div className="ntcog-entity-card__detail text-break" key={i}>
              {d.label && (
                <>
                  <span className="ntcog-entity-card__detail-label">{d.label}</span>
                  <span className="ntcog-entity-card__detail-sep" aria-hidden> · </span>
                </>
              )}
              {d.value}
            </div>
          ))}
        </div>
      )}

      {status && <div className="ntcog-entity-card__status">{status}</div>}

      {primaryAction && <div className="ntcog-entity-card__action">{primaryAction}</div>}
    </article>
  );
};

export default EntityCard;
